import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Tabs, Tab, Card, CardContent, Typography, Chip, IconButton,
  TextField, Button, Collapse, Stack, Badge, Tooltip, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Paper, Divider, List, ListItem,
  ListItemButton, ListItemText, Alert,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  ExpandMore, ExpandLess, Send, Add, Refresh,
  CheckCircle, Cancel, Chat as ChatIcon, Psychology,
  TrendingDown, TrendingUp, Remove, Delete, AttachFile, Close,
  InfoOutlined, VerifiedUserOutlined, ReportProblemOutlined, LinkOutlined
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const AI_API = '/api/ai';

const MAX_FILES = 10;
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_TOTAL_BYTES = 50 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function getAuthHeadersNoCT() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

function attachmentSrc(url) {
  const token = localStorage.getItem('token');
  if (!url) return '';
  return `${url}${url.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}`;
}

function formatBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} КБ`;
  return `${(n / 1024 / 1024).toFixed(1)} МБ`;
}

// ==================== WORKBENCH TAB ====================

function SubgroupCard({ card, onAnalyze, onApply, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  const [recs, setRecs] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);

  const severityColor = { red: 'error', yellow: 'warning', green: 'success' };
  const m = card.metrics;

  const loadRecs = async () => {
    if (recs.length) return;
    setLoadingRecs(true);
    try {
      const res = await fetch(
        `${AI_API}/recommendations?podgruppa=${encodeURIComponent(card.podgruppa)}&organization=${encodeURIComponent(card.organization)}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      setRecs(data.recommendations || []);
    } catch (e) { console.error(e); }
    setLoadingRecs(false);
  };

  const handleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) loadRecs();
  };

  return (
    <Card sx={{ borderLeft: 4, borderColor: `${severityColor[card.severity]}.main`, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ pb: expanded ? 2 : '12px !important', pt: 1.5, flex: 1 }}>
        <Box sx={{ cursor: 'pointer' }} onClick={handleExpand}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
            <Box sx={{ minWidth: 0, mr: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {card.podgruppa}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {card.gruppa} · {card.organization} · {card.articleCount} арт.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              {card.recommendations.newCount > 0 && (
                <Badge badgeContent={card.recommendations.newCount} color="primary">
                  <Psychology fontSize="small" />
                </Badge>
              )}
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </Box>
          </Box>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`${m.ordersYesterday} зак.`} />
            {m.ctr && <Chip size="small" label={`CTR ${m.ctr}%`} variant="outlined" />}
            <DeltaChip value={m.deltaWeek} label="нед" />
          </Stack>
        </Box>

        <Collapse in={expanded}>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap' }} useFlexGap>
            <MetricBox label="Заказы вчера" value={m.ordersYesterday} delta={m.deltaDay} deltaLabel="д/д" />
            <MetricBox label="Δ к неделе" value={m.deltaWeek !== null ? `${m.deltaWeek}%` : '—'} />
            <MetricBox label="7д vs пред. 7д" value={m.delta7d !== null ? `${m.delta7d}%` : '—'} />
            <MetricBox label="Выручка" value={`${(m.revenueYesterday || 0).toLocaleString()} ₽`} />
          </Stack>

          {loadingRecs && <CircularProgress size={20} />}

          {recs.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Рекомендации</Typography>
              {recs.map(rec => (
                <RecommendationCard
                  key={rec.id}
                  rec={rec}
                  onApply={onApply}
                  onDismiss={onDismiss}
                  onAnalyze={() => onAnalyze(card.podgruppa, card.organization)}
                />
              ))}
            </Box>
          )}

          <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => onAnalyze(card.podgruppa, card.organization)} startIcon={<Psychology />}>
            Полный анализ в чате
          </Button>
        </Collapse>
      </CardContent>
    </Card>
  );
}

function DeltaChip({ value, label }) {
  if (value === null || value === undefined) return null;
  const color = value > 5 ? 'success' : value < -5 ? 'error' : 'default';
  const icon = value > 0 ? <TrendingUp sx={{ fontSize: 14 }} /> : value < 0 ? <TrendingDown sx={{ fontSize: 14 }} /> : <Remove sx={{ fontSize: 14 }} />;
  return <Chip size="small" icon={icon} label={`${value > 0 ? '+' : ''}${value}% ${label || ''}`} color={color} variant="outlined" />;
}

function MetricBox({ label, value, delta, deltaLabel }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value}</Typography>
      {delta !== undefined && delta !== null && (
        <Typography variant="caption" color={delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.secondary'}>
          {delta > 0 ? '+' : ''}{delta}% {deltaLabel || ''}
        </Typography>
      )}
    </Box>
  );
}

// ==================== VERIFIER UI HELPERS ====================

const VERIFIER_STATUS_CONFIG = {
  approved: { label: 'Проверено', color: 'success', icon: <VerifiedUserOutlined sx={{ fontSize: 14 }} /> },
  needs_review: { label: 'Требует проверки', color: 'warning', icon: <ReportProblemOutlined sx={{ fontSize: 14 }} /> },
  rejected: { label: 'Отклонено', color: 'error', icon: <Cancel sx={{ fontSize: 14 }} /> },
  skipped: { label: 'Без проверки', color: 'default', icon: <InfoOutlined sx={{ fontSize: 14 }} /> },
};

const SEVERITY_CONFIG = {
  CRITICAL: { label: 'CRITICAL', color: 'error' },
  HIGH: { label: 'HIGH', color: 'warning' },
  MEDIUM: { label: 'MEDIUM', color: 'info' },
  LOW: { label: 'LOW', color: 'default' },
};

const SOURCE_TYPE_LABELS = {
  db: 'БД',
  marketplace_api: 'API МП',
  code: 'Код',
  web: 'Web',
  mpstats: 'MPSTATS',
  user_input: 'Пользователь',
};

function VerifierStatusChip({ status, iterationsCount }) {
  const cfg = VERIFIER_STATUS_CONFIG[status] || VERIFIER_STATUS_CONFIG.skipped;
  const label = iterationsCount > 0 ? `${cfg.label} (${iterationsCount} итер.)` : cfg.label;
  return (
    <Tooltip title={`Verifier: ${cfg.label}${iterationsCount ? ` · фикс-итераций: ${iterationsCount}` : ''}`}>
      <Chip
        size="small"
        color={cfg.color}
        icon={cfg.icon}
        label={label}
        variant="outlined"
        sx={{ fontWeight: 500 }}
      />
    </Tooltip>
  );
}

function WarningsTooltip({ warnings }) {
  if (!warnings || !warnings.length) return null;
  const content = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
        Замечания verifier'а:
      </Typography>
      {warnings.map((w, i) => {
        const sev = SEVERITY_CONFIG[w.severity] || SEVERITY_CONFIG.LOW;
        return (
          <Box key={i} sx={{ mb: i < warnings.length - 1 ? 1 : 0 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
              [{sev.label}] {w.category}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              {w.description}
            </Typography>
            {w.suggested_fix && (
              <Typography variant="caption" sx={{ display: 'block', color: 'grey.400', fontStyle: 'italic' }}>
                → {w.suggested_fix}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
  return (
    <Tooltip title={content} arrow placement="top" componentsProps={{ tooltip: { sx: { maxWidth: 360 } } }}>
      <IconButton size="small" sx={{ p: 0.25 }}>
        <InfoOutlined sx={{ fontSize: 16, color: 'warning.main' }} />
      </IconButton>
    </Tooltip>
  );
}

function SourcesAccordion({ sources }) {
  if (!sources || !sources.length) return null;
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        bgcolor: 'transparent',
        mt: 0.5,
        '&:before': { display: 'none' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ fontSize: 16 }} />}
        sx={{ minHeight: 28, py: 0, px: 1, '& .MuiAccordionSummary-content': { my: 0 } }}
      >
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LinkOutlined sx={{ fontSize: 14 }} />
          Источники ({sources.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1, py: 0.5 }}>
        <Stack spacing={0.5}>
          {sources.map((src, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={SOURCE_TYPE_LABELS[src.type] || src.type}
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  wordBreak: 'break-all',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {src.ref}
              </Typography>
              {src.confidence && (
                <Chip
                  size="small"
                  label={src.confidence}
                  color={src.confidence === 'high' ? 'success' : src.confidence === 'medium' ? 'warning' : 'default'}
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                />
              )}
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function RecommendationCard({ rec, onApply, onDismiss, onAnalyze }) {
  const verifierStatus = rec.verifier_status || 'skipped';
  const warnings = rec.verifier_warnings || [];
  const sources = rec.sources || (rec.payload && rec.payload.sources) || [];

  return (
    <Paper sx={{ p: 1.5, mb: 1, bgcolor: rec.status === 'new' ? 'action.hover' : 'transparent' }} variant="outlined">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {rec.status === 'new' && <Chip label="NEW" size="small" color="primary" />}
            <Chip label={rec.category} size="small" variant="outlined" />
            <Chip
              label={rec.priority}
              size="small"
              color={rec.priority === 'critical' ? 'error' : rec.priority === 'high' ? 'warning' : 'default'}
            />
            <VerifierStatusChip status={verifierStatus} iterationsCount={rec.iterations_count} />
            {warnings.length > 0 && <WarningsTooltip warnings={warnings} />}
          </Box>
          <Typography variant="body2" fontWeight={500}>{rec.title}</Typography>
          {rec.effect_score && (
            <Typography
              variant="caption"
              color={rec.effect_score === 'positive' ? 'success.main' : rec.effect_score === 'negative' ? 'error.main' : 'text.secondary'}
            >
              Эффект: {rec.effect_score}
            </Typography>
          )}
          <SourcesAccordion sources={sources} />
        </Box>
        {rec.status === 'new' && (
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Tooltip title="Применил"><IconButton size="small" color="success" onClick={() => onApply(rec.id)}><CheckCircle fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Отклонить"><IconButton size="small" color="error" onClick={() => onDismiss(rec.id)}><Cancel fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Обсудить в чате"><IconButton size="small" onClick={onAnalyze}><ChatIcon fontSize="small" /></IconButton></Tooltip>
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

function WorkbenchTab({ onSwitchToChat }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgFilter, setOrgFilter] = useState('');
  const [aiOrgs, setAiOrgs] = useState([]);

  useEffect(() => {
    fetch(`${AI_API}/dashboard/ai-accounts`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setAiOrgs(d.organizations || []))
      .catch(() => {});
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = orgFilter ? `?organization=${encodeURIComponent(orgFilter)}` : '';
      const res = await fetch(`${AI_API}/dashboard${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      setData(json);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [orgFilter]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const handleApply = async (id) => {
    await fetch(`${AI_API}/recommendations/${id}`, {
      method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ status: 'applied' })
    });
    loadDashboard();
  };

  const handleDismiss = async (id) => {
    await fetch(`${AI_API}/recommendations/${id}`, {
      method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ status: 'dismissed' })
    });
    loadDashboard();
  };

  const handleAnalyze = (podgruppa, organization) => {
    onSwitchToChat(podgruppa, organization);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  if (data?.noAccounts) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        AI-аналитика не настроена. Перейдите в Настройки &rarr; Аккаунты МП, добавьте read-only AI-токен и включите AI для нужных аккаунтов WB.
      </Alert>
    );
  }

  if (!data) return <Alert severity="info">Нет данных</Alert>;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Организация</InputLabel>
          <Select value={orgFilter} onChange={e => setOrgFilter(e.target.value)} label="Организация">
            <MenuItem value="">Все</MenuItem>
            {aiOrgs.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>
        <IconButton onClick={loadDashboard}><Refresh /></IconButton>
        <Box sx={{ flex: 1 }} />
        <Chip label={`Требуют внимания: ${data.counters.needAttention}`} color="error" variant="outlined" />
        <Chip label={`Новые рекоменд.: ${data.counters.newRecommendations}`} color="primary" variant="outlined" />
        <Chip label={`Ожидают оценки: ${data.counters.awaitingEffect}`} color="warning" variant="outlined" />
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2 }}>
        {data.cards.map(card => (
          <SubgroupCard
            key={`${card.podgruppa}|${card.organization}`}
            card={card}
            onAnalyze={handleAnalyze}
            onApply={handleApply}
            onDismiss={handleDismiss}
          />
        ))}
      </Box>

      {!data.cards.length && <Alert severity="info">Нет активных подгрупп</Alert>}
    </Box>
  );
}

// ==================== REASONING COMPONENTS ====================

const TOOL_LABELS = {
  dispatch_specialist: 'Запускаю специалиста',
  query_database: 'SQL-запрос к БД',
  get_subgroup_context: 'Загрузка контекста подгруппы',
  get_group_benchmarks: 'Загрузка бенчмарков',
  wb_api_call: 'Запрос к WB API',
  get_wb_api_docs: 'Чтение документации WB API',
  search_products: 'Поиск товаров'
};

const ROLE_LABELS = {
  seo: 'SEO-специалист', ads: 'Рекламщик', content: 'Контент-аналитик', sales: 'Аналитик продаж'
};

function ReasoningStep({ step }) {
  const [open, setOpen] = useState(false);
  const hasDetails = step.args || step.result;

  const icons = { thinking: '💭', tool: '🔧', specialist: '👤' };
  const icon = step.status === 'done' ? '✅' : (icons[step.type] || '•');

  return (
    <Box sx={{ py: 0.15 }}>
      <Box
        onClick={() => hasDetails && setOpen(!open)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, py: 0.15,
          cursor: hasDetails ? 'pointer' : 'default', borderRadius: 0.5,
          '&:hover': hasDetails ? { bgcolor: 'action.hover' } : {}
        }}
      >
        <Typography component="span" sx={{ fontSize: '0.7rem', lineHeight: 1, flexShrink: 0 }}>{icon}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, lineHeight: 1.3, fontSize: '0.75rem' }}>
          {step.summary}
        </Typography>
        {hasDetails && (
          <ExpandMore sx={{ fontSize: 12, color: 'text.disabled', transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
        )}
      </Box>
      {hasDetails && (
        <Collapse in={open}>
          <Box sx={{ ml: 2, mr: 0.5, mt: 0.25, mb: 0.5 }}>
            {step.args && (
              <Box sx={{
                fontFamily: 'monospace', fontSize: '0.68rem', bgcolor: 'grey.100',
                p: 0.75, borderRadius: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                maxHeight: 160, overflow: 'auto', lineHeight: 1.4
              }}>
                {step.args}
              </Box>
            )}
            {step.result && (
              <Box sx={{
                fontFamily: 'monospace', fontSize: '0.68rem', bgcolor: 'grey.50', color: 'text.secondary',
                p: 0.75, borderRadius: 0.5, mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                maxHeight: 160, overflow: 'auto', lineHeight: 1.4
              }}>
                {step.result}
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

function ReasoningBlock({ steps, isLive }) {
  const [expanded, setExpanded] = useState(isLive);

  useEffect(() => { setExpanded(isLive); }, [isLive]);

  if (!steps?.length) return null;

  const n = steps.length;
  const stepsWord = n === 1 ? 'шаг' : (n >= 2 && n <= 4) ? 'шага' : 'шагов';

  return (
    <Box sx={{ mb: 1, borderLeft: 2, borderColor: isLive ? 'primary.main' : 'grey.300', pl: 1, py: 0.25 }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}
      >
        <Psychology sx={{ fontSize: 14, color: isLive ? 'primary.main' : 'text.secondary' }} />
        <Typography variant="caption" color={isLive ? 'primary.main' : 'text.secondary'} fontWeight={500} sx={{ fontSize: '0.75rem' }}>
          {isLive ? 'Анализирую...' : `Ход мысли (${n} ${stepsWord})`}
        </Typography>
        <ExpandMore sx={{
          fontSize: 14, color: 'text.disabled',
          transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s'
        }} />
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          {steps.map((step, i) => <ReasoningStep key={i} step={step} />)}
        </Box>
      </Collapse>
    </Box>
  );
}

const mdStyles = {
  minWidth: 0,
  '& p': { m: 0, mb: 0.5 },
  '& p:last-child': { mb: 0 },
  '& h1,h2,h3,h4': { mt: 1.5, mb: 0.5, fontSize: '0.95rem', fontWeight: 600 },
  '& ul,ol': { pl: 2.5, my: 0.5 },
  '& li': { mb: 0.25 },
  '& code': {
    bgcolor: 'grey.200',
    px: 0.5,
    borderRadius: 0.5,
    fontSize: '0.8rem',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  '& pre': {
    maxWidth: '100%',
    bgcolor: 'grey.200',
    p: 1,
    borderRadius: 1,
    overflowX: 'auto',
    my: 1,
  },
  '& pre code': {
    display: 'block',
    minWidth: 0,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  '& strong': { fontWeight: 600 },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    my: 1,
    fontSize: '0.8rem',
    lineHeight: 1.4,
    display: 'block',
    overflowX: 'auto',
  },
  '& thead': {
    bgcolor: 'grey.100',
  },
  '& th': {
    fontWeight: 600,
    textAlign: 'left',
    px: 1,
    py: 0.5,
    borderBottom: '2px solid',
    borderColor: 'divider',
    whiteSpace: 'nowrap',
  },
  '& td': {
    px: 1,
    py: 0.5,
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& tr:hover td': {
    bgcolor: 'action.hover',
  },
  fontSize: '0.875rem',
  lineHeight: 1.6,
};

// ==================== CHAT TAB ====================

function ChatTab({ initialPodgruppa, initialOrganization }) {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [specialists, setSpecialists] = useState([]);
  const [podgruppa, setPodgruppa] = useState(initialPodgruppa || '');
  const [gruppa, setGruppa] = useState('');
  const [organization, setOrganization] = useState(initialOrganization || '');
  const [aiOrgs, setAiOrgs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subgroupList, setSubgroupList] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);   // [{file, previewUrl, id, error?}]
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const messagesEnd = useRef(null);
  const sessionRef = useRef(null);
  const stepsRef = useRef([]);
  const fileInputRef = useRef(null);

  const addFiles = useCallback((newFiles) => {
    setUploadError('');
    const arr = Array.from(newFiles || []);
    if (!arr.length) return;
    setPendingFiles(prev => {
      const acc = [...prev];
      let totalBytes = acc.reduce((s, f) => s + (f.file.size || 0), 0);
      for (const f of arr) {
        if (acc.length >= MAX_FILES) {
          setUploadError(`Можно прикрепить максимум ${MAX_FILES} файлов`);
          break;
        }
        if (!ACCEPTED_TYPES.includes(f.type)) {
          setUploadError(`Файл ${f.name}: поддерживаются только JPG, PNG, WEBP, GIF`);
          continue;
        }
        if (f.size > MAX_FILE_BYTES) {
          setUploadError(`Файл ${f.name} превышает лимит 20 МБ`);
          continue;
        }
        if (totalBytes + f.size > MAX_TOTAL_BYTES) {
          setUploadError(`Превышен общий лимит 50 МБ`);
          break;
        }
        totalBytes += f.size;
        acc.push({
          file: f,
          previewUrl: URL.createObjectURL(f),
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        });
      }
      return acc;
    });
  }, []);

  const removeFile = useCallback((id) => {
    setPendingFiles(prev => {
      const target = prev.find(f => f.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(f => f.id !== id);
    });
  }, []);

  const clearPendingFiles = useCallback(() => {
    setPendingFiles(prev => {
      prev.forEach(f => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
      return [];
    });
    setUploadError('');
  }, []);

  // cleanup при unmount
  useEffect(() => () => {
    pendingFiles.forEach(f => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGroups = useCallback(async (org) => {
    try {
      const params = new URLSearchParams({ mp: '1' });
      if (org) params.set('organization', org);
      const res = await fetch(`/api/filters/groups?${params}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setGroups((data.groups || []).filter(g => g && g !== 'отсутствует'));
    } catch { setGroups([]); }
  }, []);

  const loadSubgroups = useCallback(async (grp, org) => {
    if (!grp) { setSubgroupList([]); return; }
    try {
      const params = new URLSearchParams({ mp: '1', gruppa: grp });
      if (org) params.set('organization', org);
      const res = await fetch(`/api/filters/subgroups?${params}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setSubgroupList(data.subgroups || []);
    } catch { setSubgroupList([]); }
  }, []);

  useEffect(() => {
    loadSessions();
    fetch(`${AI_API}/dashboard/ai-accounts`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setAiOrgs(d.organizations || []))
      .catch(() => {});
    loadGroups('');
  }, [loadGroups]);

  useEffect(() => {
    if (initialPodgruppa) setPodgruppa(initialPodgruppa);
    if (initialOrganization) setOrganization(initialOrganization);
  }, [initialPodgruppa, initialOrganization]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const res = await fetch(`${AI_API}/sessions`, { headers: getAuthHeaders() });
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch (e) { console.error(e); }
  };

  const loadSession = async (id) => {
    try {
      const res = await fetch(`${AI_API}/sessions/${id}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.session) {
        setCurrentSession(id);
        sessionRef.current = id;
        setMessages((data.session.messages || []).filter(m => m.role === 'user' || m.role === 'assistant'));
        setPodgruppa(data.session.podgruppa || '');
        setOrganization(data.session.organization || '');
      }
    } catch (e) { console.error(e); }
  };

  const newChat = () => {
    setCurrentSession(null);
    sessionRef.current = null;
    setMessages([]);
    setSpecialists([]);
    clearPendingFiles();
  };

  const deleteChat = async (id, e) => {
    e.stopPropagation();
    try {
      await fetch(`${AI_API}/sessions/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (currentSession === id) newChat();
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch (err) { console.error(err); }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if ((!text && pendingFiles.length === 0) || loading) return;
    setInput('');
    setLoading(true);
    setSpecialists([]);
    stepsRef.current = [];

    // Снимаем превьюшки для отображения в сообщении пользователя (локально, без auth-URL)
    const localAttachments = pendingFiles.map(f => ({
      filename: f.file.name,
      originalName: f.file.name,
      mimetype: f.file.type,
      size: f.file.size,
      _localPreview: f.previewUrl,
    }));

    setMessages(prev => [...prev, {
      role: 'user',
      content: text,
      ...(localAttachments.length ? { attachments: localAttachments } : {}),
    }]);

    const filesToSend = pendingFiles.map(f => f.file);
    setPendingFiles([]);  // НЕ revoke URLs, они ещё нужны в messages
    setUploadError('');

    const upsertAssistant = (updater) => {
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last._live) {
          return [...prev.slice(0, -1), updater({ ...last })];
        }
        return [...prev, updater({ role: 'assistant', content: '', reasoning: [], _live: true })];
      });
    };

    const addStep = (step) => {
      stepsRef.current = [...stepsRef.current, step];
      upsertAssistant(msg => ({ ...msg, reasoning: [...stepsRef.current] }));
    };

    try {
      const fd = new FormData();
      fd.append('message', text);
      if (sessionRef.current) fd.append('sessionId', sessionRef.current);
      if (podgruppa) fd.append('podgruppa', podgruppa);
      if (gruppa) fd.append('gruppa', gruppa);
      if (organization) fd.append('organization', organization);
      for (const f of filesToSend) fd.append('files', f, f.name);

      const res = await fetch(`${AI_API}/chat`, {
        method: 'POST',
        headers: getAuthHeadersNoCT(),  // multipart: НЕ ставим Content-Type, browser сам выставит boundary
        body: fd,
      });

      if (!res.ok && res.headers.get('content-type')?.includes('application/json')) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const payload = JSON.parse(line.slice(6));
              switch (currentEvent) {
                case 'session':
                  setCurrentSession(payload.sessionId);
                  sessionRef.current = payload.sessionId;
                  break;

                case 'attachments':
                  // backend сообщил настоящие URL для прикреплённых файлов;
                  // прикладываем их к последнему user-сообщению (которое мы добавили в messages),
                  // чтобы при reload сессии картинки подгружались с сервера
                  setMessages(prev => {
                    const idx = prev.findLastIndex(m => m.role === 'user');
                    if (idx < 0) return prev;
                    const items = payload.items || [];
                    const oldAtt = prev[idx].attachments || [];
                    const merged = items.map((it, i) => ({
                      ...it,
                      _localPreview: oldAtt[i]?._localPreview || null,
                    }));
                    const next = [...prev];
                    next[idx] = { ...next[idx], attachments: merged };
                    return next;
                  });
                  break;

                case 'thinking':
                  if (payload.step) addStep({ type: 'thinking', summary: payload.step });
                  break;

                case 'specialist_start':
                  setSpecialists(prev => [...prev, { role: payload.role, name: payload.name, status: 'working' }]);
                  addStep({ type: 'specialist', summary: `${payload.name}: анализирует...`, role: payload.role, status: 'working' });
                  break;

                case 'specialist_done': {
                  setSpecialists(prev => prev.map(s => s.role === payload.role ? { ...s, status: 'done' } : s));
                  const si = stepsRef.current.findLastIndex(s => s.type === 'specialist' && s.role === payload.role);
                  if (si >= 0) {
                    stepsRef.current[si] = { ...stepsRef.current[si], summary: `${payload.name}: готов`, status: 'done' };
                    upsertAssistant(msg => ({ ...msg, reasoning: [...stepsRef.current] }));
                  }
                  break;
                }

                case 'tool_call': {
                  const label = TOOL_LABELS[payload.tool] || payload.tool;
                  const roleSuffix = payload.role && payload.role !== 'orchestrator'
                    ? ` (${ROLE_LABELS[payload.role] || payload.role})`
                    : '';
                  let args = '';
                  try {
                    const p = JSON.parse(payload.arguments);
                    if (payload.tool === 'query_database') args = p.sql || '';
                    else if (payload.tool === 'wb_api_call') args = `${p.method || 'GET'} ${p.url}`;
                    else if (payload.tool === 'dispatch_specialist')
                      args = `${ROLE_LABELS[p.role] || p.role}: ${(p.task || '').substring(0, 200)}`;
                    else if (payload.tool === 'get_subgroup_context')
                      args = `${p.podgruppa || ''}${p.organization ? ' / ' + p.organization : ''}`;
                    else args = JSON.stringify(p, null, 2);
                  } catch {}
                  addStep({ type: 'tool', summary: `${label}${roleSuffix}`, args: args || null, result: null });
                  break;
                }

                case 'tool_result': {
                  for (let i = stepsRef.current.length - 1; i >= 0; i--) {
                    if (stepsRef.current[i].type === 'tool' && !stepsRef.current[i].result) {
                      stepsRef.current[i] = { ...stepsRef.current[i], result: payload.preview || '' };
                      break;
                    }
                  }
                  upsertAssistant(msg => ({ ...msg, reasoning: [...stepsRef.current] }));
                  break;
                }

                case 'content':
                  if (payload.delta) {
                    upsertAssistant(msg => ({ ...msg, content: payload.delta, _live: false }));
                  }
                  stepsRef.current = [];
                  break;

                case 'error':
                  upsertAssistant(msg => ({
                    ...msg,
                    content: `Ошибка: ${payload.message || 'неизвестная'}`,
                    _live: false
                  }));
                  stepsRef.current = [];
                  break;

                case 'done':
                  break;
              }
            } catch { /* ignore parse errors */ }
            currentEvent = null;
          } else if (line === '') {
            currentEvent = null;
          }
        }
      }
    } catch (e) {
      upsertAssistant(msg => ({
        ...msg,
        content: `Ошибка подключения: ${e.message}`,
        _live: false
      }));
    }

    setLoading(false);
    loadSessions();
  };

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file && ACCEPTED_TYPES.includes(file.type)) files.push(file);
      }
    }
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  }, [addFiles]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  }, [dragOver]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    if (e.target === e.currentTarget) setDragOver(false);
  }, []);

  return (
    <Box sx={{ display: 'flex', width: '100%', minWidth: 0, height: 'calc(100vh - 200px)', gap: 2 }}>
      <Paper sx={{ width: 260, flexShrink: 0, overflow: 'auto' }} variant="outlined">
        <Box sx={{ p: 1.5 }}>
          <Button fullWidth variant="contained" size="small" startIcon={<Add />} onClick={newChat}>
            Новый чат
          </Button>
        </Box>
        <Divider />
        <List dense>
          {sessions.map(s => (
            <ListItem key={s.id} disablePadding secondaryAction={
              <IconButton edge="end" size="small" onClick={e => deleteChat(s.id, e)}
                sx={{ opacity: 0.3, '&:hover': { opacity: 1, color: 'error.main' } }}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            }>
              <ListItemButton selected={currentSession === s.id} onClick={() => loadSession(s.id)} sx={{ pr: 5 }}>
                <ListItemText
                  primary={s.summary || s.podgruppa || 'Чат'}
                  secondary={new Date(s.created_at).toLocaleDateString('ru')}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          {!sessions.length && (
            <ListItem>
              <ListItemText
                primary="Нет сессий"
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', textAlign: 'center' }}
              />
            </ListItem>
          )}
        </List>
      </Paper>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Организация</InputLabel>
            <Select value={organization} label="Организация" onChange={e => {
              const org = e.target.value;
              setOrganization(org);
              setGruppa(''); setPodgruppa(''); setSubgroupList([]);
              loadGroups(org);
            }}>
              <MenuItem value="">Все</MenuItem>
              {aiOrgs.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Группа</InputLabel>
            <Select value={gruppa} label="Группа" onChange={e => {
              const g = e.target.value;
              setGruppa(g); setPodgruppa('');
              loadSubgroups(g, organization);
            }}>
              <MenuItem value=""><em>Не выбрана</em></MenuItem>
              {groups.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Подгруппа</InputLabel>
            <Select value={podgruppa} label="Подгруппа" disabled={!gruppa}
              onChange={e => setPodgruppa(e.target.value)}>
              <MenuItem value=""><em>Общий анализ</em></MenuItem>
              {subgroupList.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>

        <Paper sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: 2, mb: 1 }} variant="outlined">
          {messages.length === 0 && (
            <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
              <Psychology sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography>Задайте вопрос AI-аналитику</Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Выберите подгруппу, группу или оставьте пустым для анализа кабинета целиком
              </Typography>
            </Box>
          )}
          {messages.map((msg, i) => (
            <Box key={i} sx={{ width: '100%', minWidth: 0, mb: 2, display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <Paper
                sx={{
                  maxWidth: '80%',
                  minWidth: 0,
                  overflow: 'hidden',
                  p: 1.5,
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.50',
                  color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                }}
                elevation={0}
              >
                {msg.role === 'assistant' ? (
                  <>
                    {msg.reasoning?.length > 0 && (
                      <ReasoningBlock steps={msg.reasoning} isLive={!!msg._live} />
                    )}
                    {msg._live ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: msg.reasoning?.length ? 0.5 : 0 }}>
                        <CircularProgress size={14} />
                        <Typography variant="caption" color="text.secondary">
                          Анализирую...
                        </Typography>
                      </Box>
                    ) : msg.content ? (
                      <Box sx={mdStyles}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                      </Box>
                    ) : null}
                  </>
                ) : (
                  <>
                    {msg.attachments?.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: msg.content ? 1 : 0 }}>
                        {msg.attachments.map((att, ai) => {
                          const src = att._localPreview || (att.url ? attachmentSrc(att.url) : null);
                          if (!src) return null;
                          return (
                            <Box
                              key={ai}
                              component="a"
                              href={src}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'block',
                                borderRadius: 1,
                                overflow: 'hidden',
                                bgcolor: 'rgba(0,0,0,0.1)',
                                cursor: 'zoom-in',
                                lineHeight: 0,
                              }}
                            >
                              <Box
                                component="img"
                                src={src}
                                alt={att.originalName || att.filename || 'image'}
                                sx={{ maxWidth: 240, maxHeight: 240, display: 'block' }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    {msg.content && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                        {msg.content}
                      </Typography>
                    )}
                  </>
                )}
              </Paper>
            </Box>
          ))}
          <div ref={messagesEnd} />
        </Paper>

        {pendingFiles.length > 0 && (
          <Paper
            variant="outlined"
            sx={{ mb: 1, p: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}
          >
            {pendingFiles.map(pf => (
              <Box key={pf.id} sx={{ position: 'relative', width: 64, height: 64, borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
                <Box
                  component="img"
                  src={pf.previewUrl}
                  alt={pf.file.name}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <Tooltip title={`${pf.file.name} · ${formatBytes(pf.file.size)}`}>
                  <IconButton
                    size="small"
                    onClick={() => removeFile(pf.id)}
                    sx={{
                      position: 'absolute', top: 2, right: 2,
                      bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                      width: 18, height: 18,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
                    }}
                  >
                    <Close sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Paper>
        )}

        {uploadError && (
          <Alert severity="warning" sx={{ mb: 1, py: 0.25 }} onClose={() => setUploadError('')}>
            {uploadError}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            position: 'relative',
            ...(dragOver && {
              outline: '2px dashed',
              outlineColor: 'primary.main',
              outlineOffset: 4,
              borderRadius: 1,
              bgcolor: 'action.hover',
            }),
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            multiple
            hidden
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <Tooltip title="Прикрепить изображения (jpg, png, webp, gif)">
            <span>
              <IconButton
                color="default"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                sx={{ flexShrink: 0 }}
              >
                <AttachFile />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            fullWidth size="small"
            placeholder={dragOver ? 'Отпустите файлы для прикрепления' : 'Введите сообщение... (можно вставить картинку из буфера)'}
            value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            onPaste={handlePaste}
            disabled={loading}
            multiline
            maxRows={3}
          />
          <Button
            variant="contained"
            onClick={sendMessage}
            disabled={loading || (!input.trim() && pendingFiles.length === 0)}
            sx={{ minWidth: 48 }}
          >
            <Send />
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ==================== MAIN PAGE ====================

export default function AiAnalyst() {
  const [tab, setTab] = useState(0);
  const [chatPodgruppa, setChatPodgruppa] = useState('');
  const [chatOrganization, setChatOrganization] = useState('');

  const switchToChat = (podgruppa, organization) => {
    setChatPodgruppa(podgruppa);
    setChatOrganization(organization);
    setTab(1);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>AI-аналитик</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Рабочий стол" />
        <Tab label="Чат" />
      </Tabs>
      {tab === 0 && <WorkbenchTab onSwitchToChat={switchToChat} />}
      {tab === 1 && <ChatTab initialPodgruppa={chatPodgruppa} initialOrganization={chatOrganization} />}
    </Box>
  );
}
